import { Button, Group, GroupProps } from '@mantine/core';
import { useEditedWorkflowActions } from '@/app/workflows/contexts/editedWorkflows';
import { createWorkflow, updateWorkflow } from '@/app/workflows/actions/workflows';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showErrorNotification, showSuccessNotification } from '@/app/components/shared/notifications';
import { Workflow } from '@/app/workflows/actions/interfaces';
import { useSessionStorage } from '@mantine/hooks';
import { NEWLY_CREATED_WORKFLOWS_IDS } from '@/app/workflows/constants';
import { WorkflowType } from '@/app/workflows/actions/constants';
import { useWorkflowTableUtilsContext } from '@/app/workflows//components/table/contexts/TableUitlsContext';
import { useWorkflowsParams, WORKFLOWS_QUERY_MAIN_KEY } from '@/app/workflows/components/table/query/useWorkflowsQuery';
import { useRouter, useSearchParams } from 'next/navigation';
import { WorkflowTab } from '@/app/workflows/utils/navigation';
import { useWorkflow } from '../../../contexts/getWorkflow';
import styles from '@/app/components/shared/table/workflowStyles.module.css';

type EditedActionButtonsProps = {
  workflowId: number;
  onWorkflowAdded?: (workflowId: number) => void;
} & GroupProps;

export function EditedActionButtons({ workflowId, onWorkflowAdded, ...props }: EditedActionButtonsProps) {
  const { removeWorkflowFromEdited } = useEditedWorkflowActions();
  const { setExpandedValue, setPage, scrollToTop } = useWorkflowTableUtilsContext();
  const queryClient = useQueryClient();
  const [ids, setIds] = useSessionStorage({
    key: NEWLY_CREATED_WORKFLOWS_IDS,
    defaultValue: [] as number[],
  });
  const router = useRouter();
  const searchParams = useSearchParams();
  const { activeTab, search } = useWorkflowsParams();
  const { getWorkflow, resetWorkflow } = useWorkflow();

  const editedWorkflow = getWorkflow();

  const { mutate: saveAsNew, isPending: isSaveAsNewPending } = useMutation({
    mutationFn: async () => {
      if (!editedWorkflow) {
        throw new Error('Workflow not found');
      }
      return createWorkflow({
        config: editedWorkflow.config,
        description: editedWorkflow.description,
        name: editedWorkflow.name + ' (copy)',
        meta: editedWorkflow.meta,
      });
    },
    onError: (error) => {
      showErrorNotification({
        title: 'Failed to save workflow',
        message: error.message,
      });
    },
    onSuccess: async (workflow: Workflow) => {
      queryClient.invalidateQueries({ queryKey: [WORKFLOWS_QUERY_MAIN_KEY] });

      setExpandedValue(workflow.id, true);
      scrollToTop();
      setIds([...ids, workflow.id]);
      showSuccessNotification({
        title: 'Workflow saved!',
        message: 'New workflow has been saved successfully',
      });
      onWorkflowAdded?.(workflow.id);

      removeWorkflowFromEdited(workflowId, true);
      resetWorkflow();

      if (editedWorkflow?.id) {
        setExpandedValue(editedWorkflow?.id, false);
      }

      if (
        [WorkflowTab.DEVICE, WorkflowTab.GENERAL, WorkflowTab.APPLICATION].includes(
          searchParams.get('activeTab') as WorkflowTab
        )
      ) {
        router.push(`/workflows?activeTab=${WorkflowTab.CUSTOM}`);
      } else {
        return setPage?.(1);
      }
    },
  });

  const { mutate: save, isPending: isSavePending } = useMutation({
    onMutate: () => {
      if (!editedWorkflow) {
        throw new Error('Workflow not found');
      }
      const snapshot = queryClient.getQueryData<Workflow[]>([WORKFLOWS_QUERY_MAIN_KEY, activeTab, search]);
      queryClient.setQueryData<Workflow[]>([WORKFLOWS_QUERY_MAIN_KEY, activeTab, search], (data) => {
        if (!data) {
          return [];
        }
        return data.map((workflow) => {
          if (workflow.id === editedWorkflow.id) {
            return editedWorkflow;
          }
          return workflow;
        });
      });

      return () => {
        queryClient.setQueryData([WORKFLOWS_QUERY_MAIN_KEY, activeTab, search], snapshot);
      };
    },
    mutationFn: async () => {
      if (!editedWorkflow) {
        throw new Error('Workflow not found');
      }

      return await updateWorkflow(editedWorkflow);
    },
    onError: (error, _variables, rollback) => {
      showErrorNotification({
        title: 'Failed to update workflow',
        message: error.message,
      });
      rollback?.();
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes(WORKFLOWS_QUERY_MAIN_KEY),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes(WORKFLOWS_QUERY_MAIN_KEY),
      });

      showSuccessNotification({
        title: 'Preset updated!',
        message: 'New 3D Preset has been updated successfully.',
      });
      removeWorkflowFromEdited(workflowId);
    },
  });

  if (!editedWorkflow) {
    return null;
  }

  const isWorkflowCustom = editedWorkflow.clientMetadata.type === WorkflowType.CUSTOM;

  return (
    <Group
      className={
        styles[
          'edited-action-buttons'
        ] /* Please note in style file that there is styling depending on presence of this here. Not a perfect solution, but allows for big performance gain.  */
      }
      gap='xs'
      w='400px'
      justify='flex-end'
      flex='space-around'
      {...props}
    >
      <Button
        variant='transparent'
        onClick={() => {
          removeWorkflowFromEdited(workflowId, true);
          resetWorkflow();
        }}
      >
        Clear changes
      </Button>
      {isWorkflowCustom ? (
        <Button variant='filled' loading={isSavePending} onClick={() => save()}>
          Save
        </Button>
      ) : null}
      <Button
        variant={isWorkflowCustom ? 'outline' : 'filled'}
        loading={isSaveAsNewPending}
        onClick={() => saveAsNew()}
      >
        Save as new
      </Button>
    </Group>
  );
}
